import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthShell from '../../components/ui/AuthShell';
import { ROUTES } from '../../config/routes';
import { APIServices } from '../../services/axiosCommon';

type State =
  | { kind: 'missing' }
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const VerifyAccountView = () => {
  const [params] = useSearchParams();
  const membersid = Number(params.get('membersid') ?? '');
  const rkey = params.get('rkey') ?? '';

  const [state, setState] = useState<State>(
    membersid && rkey ? { kind: 'pending' } : { kind: 'missing' },
  );

  useEffect(() => {
    if (state.kind !== 'pending') return;
    let cancelled = false;
    (async () => {
      try {
        await APIServices.post('/auth/verify-account', { membersid, rkey });
        if (!cancelled) setState({ kind: 'success' });
      } catch (err) {
        const message =
          (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message ?? 'Verification failed';
        if (!cancelled) setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.kind, membersid, rkey]);

  return (
    <AuthShell
      title="Verify account"
      footer={
        <Link to={ROUTES.LOGIN} className="font-medium text-tfs-primary hover:underline">
          Go to login →
        </Link>
      }
    >
      {state.kind === 'missing' && (
        <p className="text-sm text-slate-500">
          This page expects <code>?membersid</code> and <code>?rkey</code> in the URL. After registering you'll
          land here automatically.
        </p>
      )}
      {state.kind === 'pending' && (
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <Loader2 className="size-5 animate-spin text-tfs-primary" /> Verifying…
        </div>
      )}
      {state.kind === 'success' && (
        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <p className="font-medium">Account verified.</p>
            <p className="mt-1 text-xs">You can now log in.</p>
          </div>
        </div>
      )}
      {state.kind === 'error' && (
        <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3">
          <XCircle className="mt-0.5 size-5 shrink-0 text-red-600" />
          <div className="text-sm text-red-900">
            <p className="font-medium">Verification failed</p>
            <p className="mt-1 text-xs">{state.message}</p>
          </div>
        </div>
      )}
    </AuthShell>
  );
};

export default VerifyAccountView;
